import {
  QuestGroupDraft,
  StepDto,
  StepUnlockMode,
  StepUpsertDto,
  StepTargetType,
  TargetDraft,
  narrowStepTarget,
  questDraftToUpsert,
} from '@/common/models/gamification'
import {
  createQuest,
  createQuestGroup,
  createStandaloneQuest,
  createStep,
  deleteQuest,
  deleteQuestGroup,
  updateQuest,
  updateQuestGroup,
  updateStep,
} from '@/core/services/gamification'

import { reconcileQuestGroupTags, reconcileQuestTags } from './tagAssociations'

export interface StepFields {
  unlockMode: StepUnlockMode
  badgeId: string
  /** Optional 42x42 icon (uploaded image URL); empty string clears it. */
  iconRef: string
  active: boolean
  /** Active but excluded from the consumer roadmap UI. */
  hidden: boolean
  /** Preserve on edit; undefined on create lets the server append. */
  ordinal?: number
}

interface SaveStepParams {
  roadmapId: string
  /** The step being edited, or null when creating a new step. */
  original: StepDto | null
  stepFields: StepFields
  draft: TargetDraft
  /**
   * Called as entities are persisted so their new ids are written back into the
   * caller's draft state. If a later call fails, a retry updates (not re-creates)
   * the already-persisted quest/group, keeping the operation idempotent.
   */
  onDraftChange?: (draft: TargetDraft) => void
}

interface ResolvedTarget {
  targetType: StepTargetType | null
  targetId: string | null
}

/**
 * Orchestrates persisting a step and its inline-authored target (client-side, since the
 * admin API is granular CRUD). Order of operations:
 *   1. Resolve the target — create/update the standalone quest, or the group + its quests,
 *      or pick the roadmap — writing created ids back into the draft.
 *   2. Create or update the step pointing at the resolved target.
 *   3. If editing and the step previously owned a *different* QUEST/QUEST_GROUP target,
 *      delete that old target (the step no longer references it, so the backend's
 *      "still referenced" guard passes). ROADMAP targets are never deleted (shared).
 */
export const saveStep = async ({
  roadmapId,
  original,
  stepFields,
  draft,
  onDraftChange,
}: SaveStepParams): Promise<void> => {
  const resolved = await resolveTarget(roadmapId, draft, onDraftChange)

  const stepDto: StepUpsertDto = {
    ordinal: stepFields.ordinal,
    unlockMode: stepFields.unlockMode,
    targetType: resolved.targetType,
    targetId: resolved.targetId,
    badgeId: stepFields.badgeId || null,
    iconRef: stepFields.iconRef || null,
    active: stepFields.active,
    hidden: stepFields.hidden,
  }

  if (original) await updateStep(original.id, stepDto)
  else await createStep(roadmapId, stepDto)

  if (original) await cleanupReplacedTarget(original, resolved)
}

const resolveTarget = async (
  roadmapId: string,
  draft: TargetDraft,
  onDraftChange?: (draft: TargetDraft) => void,
): Promise<ResolvedTarget> => {
  switch (draft.kind) {
    case 'NONE':
      return { targetType: null, targetId: null }

    case 'ROADMAP':
      return { targetType: 'ROADMAP', targetId: draft.roadmapId }

    case 'QUEST': {
      const quest = draft.quest
      if (!quest) return { targetType: null, targetId: null }
      if (quest.id) {
        await updateQuest(quest.id, questDraftToUpsert(quest))
        await reconcileQuestTags(quest.id, quest.tags ?? [])
        return { targetType: 'QUEST', targetId: quest.id }
      }
      const { data } = await createStandaloneQuest(roadmapId, questDraftToUpsert(quest))
      await reconcileQuestTags(data.id, quest.tags ?? [])
      onDraftChange?.({ kind: 'QUEST', quest: { ...quest, id: data.id } })
      return { targetType: 'QUEST', targetId: data.id }
    }

    case 'QUEST_GROUP':
      return resolveGroupTarget(roadmapId, draft.group, onDraftChange)
  }
}

const resolveGroupTarget = async (
  roadmapId: string,
  group: QuestGroupDraft,
  onDraftChange?: (draft: TargetDraft) => void,
): Promise<ResolvedTarget> => {
  // Track the draft as ids come back so retries update instead of duplicating.
  let working: QuestGroupDraft = group
  const commit = (next: QuestGroupDraft) => {
    working = next
    onDraftChange?.({ kind: 'QUEST_GROUP', group: next })
  }

  // 1. Group itself.
  let groupId = working.id
  if (groupId) {
    await updateQuestGroup(groupId, working.meta)
  } else {
    const { data } = await createQuestGroup(roadmapId, working.meta)
    groupId = data.id
    commit({ ...working, id: groupId })
  }
  await reconcileQuestGroupTags(groupId, working.tags ?? [])

  // 2. Quests — create/update, ordinal by list position.
  for (let i = 0; i < working.quests.length; i++) {
    const quest = working.quests[i]
    const dto = questDraftToUpsert({ ...quest, ordinal: i })
    if (quest.id) {
      await updateQuest(quest.id, dto)
      await reconcileQuestTags(quest.id, quest.tags ?? [])
    } else {
      const { data } = await createQuest(groupId, dto)
      await reconcileQuestTags(data.id, quest.tags ?? [])
      const quests = working.quests.map((q, idx) =>
        idx === i ? { ...q, id: data.id, ordinal: i } : q,
      )
      commit({ ...working, quests })
    }
  }

  // 3. Quests removed during this edit.
  for (const removedId of working.removedQuestIds) {
    await deleteQuest(removedId)
  }
  if (working.removedQuestIds.length) {
    commit({ ...working, removedQuestIds: [] })
  }

  return { targetType: 'QUEST_GROUP', targetId: groupId }
}

/**
 * Deletes the step's previously-owned QUEST/QUEST_GROUP target when it has been
 * replaced by a different one. The step has already been updated to point at the new
 * target, so the backend's referential guard allows the delete.
 */
const cleanupReplacedTarget = async (
  original: StepDto,
  resolved: ResolvedTarget,
): Promise<void> => {
  const old = narrowStepTarget(original)
  if (old.targetType !== 'QUEST' && old.targetType !== 'QUEST_GROUP') return

  const oldId = old.target?.id ?? original.targetId ?? null
  if (!oldId) return

  const unchanged =
    old.targetType === resolved.targetType && oldId === resolved.targetId
  if (unchanged) return

  if (old.targetType === 'QUEST') await deleteQuest(oldId)
  else await deleteQuestGroup(oldId)
}
